import { useAuth } from '@/auth/AuthContext';
import { FALLBACK_LANGUAGE_SCHEMA } from '@/constants';
import { getLanguagesAvailableForTranslation } from '@/services/asyncActions';
import { fetchSchema, setLanguageSchema } from '@/store/schemaSlice';
import { RootState, useAppDispatch } from '@/store/store';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import Footer from './Footer/Footer';
import Header from './Header/Header';
import styles from './Layout.module.scss';

interface LayoutProps {
  children?: React.ReactNode;
  background?: string;
  isFullWidth?: boolean;
}

const MAX_CONTAINER_WIDTH = '1350px';
const Layout: React.FC<LayoutProps> = ({
  children,
  background,
  isFullWidth = false,
}) => {
  const initialLoad = useRef(false);
  const dispatch = useAppDispatch();
  const maxWidth = isFullWidth
    ? {
        xs: '100%',
        sm: '100%',
        md: '100%',
        lg: '100%',
        xl: '100%',
      }
    : {
        xs: '100%',
        sm: '100%',
        md: '100%',
        lg: MAX_CONTAINER_WIDTH,
        xl: MAX_CONTAINER_WIDTH,
      };

  const containerStyles = {
    maxWidth: maxWidth,
  };

  const { isAuthenticated } = useAuth();
  const formSchema = useSelector((state: RootState) => state.schema.schemaData);

  useEffect(() => {
    if (isAuthenticated == false) {
      return;
    }

    if (initialLoad.current == true) {
      return;
    }

    initialLoad.current = true;

    const loadLanguageSchema = async () => {
      try {
        const response = await getLanguagesAvailableForTranslation();
        if (response != null && response.length > 0) {
          dispatch(setLanguageSchema(response));
          return;
        }

        dispatch(setLanguageSchema(FALLBACK_LANGUAGE_SCHEMA));
      } catch (error) {
        dispatch(setLanguageSchema(FALLBACK_LANGUAGE_SCHEMA));
      }
    };

    loadLanguageSchema();

    if (formSchema == null) {
      dispatch(fetchSchema());
    }
  }, [isAuthenticated, dispatch, formSchema]);

  return (
    <Box className={styles.layout}>
      <CssBaseline />
      <Box component="header" className={styles.header}>
        <Header />
      </Box>
      <Box
        component="main"
        className={styles.main}
        sx={{ background: background || 'none' }}
      >
        <Container sx={containerStyles} disableGutters>
          {children || <Outlet />}
        </Container>
      </Box>
      <Box component="footer" className={styles.footer}>
        <Footer />
      </Box>
    </Box>
  );
};

export default Layout;
